import React, { useState, useEffect } from "react";
import { ModalBody, ModalFooter, ModalHeader, ModalPanel } from "../Modal";
import { Input, SelectInput } from "../Form";
import { Button } from "../Button";
import { useCompanies } from "../../core/utils/hooks/useCompanies";
import { customToast } from "../Toast";
import "./styles.scss";

const EditUserModal = ({ 
  isOpen, 
  onClose, 
  user, 
  onSave,
  loggedUserId 
}) => {
  const [userName, setUserName] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { companies, loading: companiesLoading } = useCompanies(loggedUserId);

  // Inicializar dados do formulário quando o modal abrir
  useEffect(() => {
    if (isOpen && user) {
      setUserName(user.name || "");
      
      // Converter empresas do usuário para formato do SelectInput
      const userCompanies = user.companies?.map(company => ({
        value: company.id || company.value,
        label: company.name || company.label
      })) || [];
      
      setSelectedCompanies(userCompanies);
      setErrors({});
    }
  }, [isOpen, user]);

  const validateForm = () => {
    const newErrors = {};

    if (!userName?.trim()) {
      newErrors.userName = "Nome é obrigatório";
    }

    if (userName && userName.trim().length < 2) {
      newErrors.userName = "Nome deve ter pelo menos 2 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      customToast("Por favor, corrija os erros no formulário", "error");
      return;
    }

    setLoading(true);

    try {
      const updatedUser = {
        ...user,
        name: userName.trim(),
        companies: selectedCompanies
      };

      await onSave(updatedUser);
      customToast("Usuário editado com sucesso!", "success");
      onClose();
    } catch (error) {
      console.error("Erro ao salvar usuário:", error);
      customToast("Erro ao salvar as alterações", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Resetar formulário ao cancelar
    setUserName("");
    setSelectedCompanies([]);
    setErrors({});
    onClose();
  };

  const handleCompaniesChange = (selectedOptions) => {
    setSelectedCompanies(selectedOptions || []);
    // Limpar erro de empresas se existir
    if (errors.companies) {
      setErrors(prev => ({ ...prev, companies: undefined }));
    }
  };

  return (
    <ModalPanel modalIsOpen={isOpen}>
      <ModalHeader 
        title="Editar Usuário" 
        closeModal={handleCancel}
      />
      
      <ModalBody>
        <div className="edit-user-modal">
          <div className="edit-user-modal__field">
            <label className="edit-user-modal__label">
              Nome do Usuário*
            </label>
            <Input
              placeholder="Digite o nome do usuário"
              value={userName}
              onChange={(e) => {
                setUserName(e.target.value);
                // Limpar erro ao digitar
                if (errors.userName) {
                  setErrors(prev => ({ ...prev, userName: undefined }));
                }
              }}
              error={!!errors.userName}
              helperText={errors.userName}
              maxLength={100}
            />
          </div>

          <div className="edit-user-modal__field">
            <label className="edit-user-modal__label">
              Empresas Associadas
            </label>
            <SelectInput
              isMulti
              options={companies}
              value={selectedCompanies}
              onChange={handleCompaniesChange}
              placeholder="Selecione as empresas"
              isLoading={companiesLoading}
              loadingMessage={() => "Carregando empresas..."}
              noOptionsMessage={() => "Nenhuma empresa encontrada"}
              isSearchable={true}
              closeMenuOnSelect={false}
              isOverModal={true}
              className="companies-select"
            />
            <small className="edit-user-modal__help-text">
              Você pode selecionar múltiplas empresas
            </small>
          </div>

          {/* Preview das empresas selecionadas */}
          {selectedCompanies.length > 0 && (
            <div className="edit-user-modal__preview">
              <h4>Empresas Selecionadas:</h4>
              <div className="selected-companies">
                {selectedCompanies.map((company, index) => (
                  <span key={company.value || index} className="company-tag">
                    {company.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          label="Cancelar"
          variant="secondary"
          handleClick={handleCancel}
          disabled={loading}
        />
        <Button
          label={loading ? "Salvando..." : "Salvar"}
          variant="primary"
          handleClick={handleSave}
          disabled={loading || !userName?.trim()}
        />
      </ModalFooter>
    </ModalPanel>
  );
};

export default EditUserModal;